import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { OAuthService, AuthConfig, OAuthEvent } from 'angular-oauth2-oidc';
import { Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { UserService } from '@cadriciel/core';

const REDIRECT_STORAGE_KEY = 'redirectURL';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    // BehaviorSubject pour maintenir l'état d'authentification
    // Permet aux composants de recevoir l'état actuel même après l'événement initial
    private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
    public isAuthenticated$: Observable<boolean> = this.isAuthenticatedSubject.asObservable();

    // Ajout de la variable pour suivre l'état du login initial
    private initialLoginComplete = false;
    private discoveryLoginPromise: Promise<boolean> | null = null;
    private platformId = inject(PLATFORM_ID);

    private oauthService = inject(OAuthService);
    private router = inject(Router);
    private userService = inject(UserService);

    constructor() {
        console.log('AuthService: Constructeur appelé');
        if (isPlatformBrowser(this.platformId)) {
            this.configureOAuth();
        }
    }

    private configureOAuth() {
        console.log('AuthService: Configuration OAuth en cours');
        const issuerUsesHttps = ((environment as any).orionUrl ?? '').toLowerCase().startsWith('https://');

        const authConfig: AuthConfig = {
            issuer: (environment as any).orionUrl ?? '', // URL du fournisseur d'identité
            clientId: (environment as any).clientId ?? '', // Identifiant client
            redirectUri: (environment as any).redirectUri ?? window.location.origin, // URL de redirection après authentification
            postLogoutRedirectUri: (environment as any).deconnexionUri ?? '', // URL de redirection après déconnexion
            scope: 'openid profile email', // Les refresh tokens standards suffisent, offline_access bloque le client
            requireHttps: issuerUsesHttps, // Autoriser HTTP uniquement pour les environnements locaux
            showDebugInformation: true,
            responseType: 'code',
        };

        this.oauthService.configure(authConfig);
        this.oauthService.setupAutomaticSilentRefresh({
            timeoutFactor: 0.8,
        });

        // Écouter les événements OAuth pour la navigation
        // IMPORTANT: On s'abonne AVANT de charger le document pour ne rien rater
        this.oauthService.events
            .pipe(filter((e: OAuthEvent) => e.type === 'token_received'))
            .subscribe(() => {
                console.log('AuthService: Token reçu, gestion de la redirection.');
                this.isAuthenticatedSubject.next(true);
                this.loadUserProfile();
                if (!this.initialLoginComplete) {
                    this.completeInitialLogin();
                }
            });

        this.oauthService.events
            .pipe(filter((e: OAuthEvent) => e.type === 'token_expires'))
            .subscribe(() => {
                console.log('AuthService: Le token va expirer, tentative de rafraîchissement.');
                this.oauthService.refreshToken()
                    .then(() => console.log('AuthService: Token rafraîchi.'))
                    .catch((error) => {
                        console.error('AuthService: Erreur refresh token, relogin.', error);
                        this.initialLoginComplete = false;
                        this.login();
                    });
            });

        this.oauthService.events
            .pipe(filter((e: OAuthEvent) => e.type === 'session_terminated' || e.type === 'logout'))
            .subscribe(() => {
                console.log('AuthService: Session terminée ou logout, redirection login.');
                this.initialLoginComplete = false;
                this.router.navigate(['/login']);
            });

        // Charger le document de découverte et essayer de se connecter
        this.ensureDiscoveryDocumentAndLogin().then((isLoggedIn) => {
            if (isLoggedIn && this.oauthService.hasValidAccessToken()) {
                console.log('AuthService: Authentifié avec succès (discovery result).');
                this.isAuthenticatedSubject.next(true);
                this.loadUserProfile();
                // Si on a manqué l'événement token_received (ex: refresh silencieux), on tente quand même la redirection initial
                if (!this.initialLoginComplete) {
                    this.completeInitialLogin();
                }
            } else {
                console.log('AuthService: Jeton invalide ou absent au démarrage.');
            }
        }).catch((error) => {
            console.error('AuthService: Erreur lors du discovery/login :', error);
        });
    }

    private loadUserProfile() {
        console.log('AuthService: Loading user profile...');
        this.userService.getCurrentUser().subscribe({
            next: (response) => {
                if (response.body) {
                    console.log('AuthService: User profile loaded', response.body);
                    this.userService.setCurrentUser(response.body);

                    // Also populate from claims if needed (e.g. for simple profile info if API returns partial)
                    const claims = this.oauthService.getIdentityClaims();
                    if (claims) {
                        this.userService.addInfoFromClaims(claims as any);
                    }
                }
            },
            error: (err) => console.error('AuthService: Error loading user profile', err)
        });
    }

    // Méthode pour déconnecter l'utilisateur
    // Méthode pour déconnecter l'utilisateur
    async logout() {
        console.log('AuthService: Déconnexion appelée.');

        const idToken = this.oauthService.getIdToken();
        const logoutUrl = this.buildLogoutUrl(idToken);

        this.initialLoginComplete = false; // Réinitialiser pour la prochaine connexion

        // Nettoyer les tokens locaux
        sessionStorage.clear();
        localStorage.removeItem('access_token');
        localStorage.removeItem('id_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('nonce');
        localStorage.removeItem('PKCE_verifier');
        localStorage.removeItem(REDIRECT_STORAGE_KEY);

        if (logoutUrl) {
            console.log('AuthService: Redirection vers logout Keycloak:', logoutUrl);
            window.location.href = logoutUrl;
        } else {
            // Fallback: utiliser la méthode de la librairie ou rediriger localement
            console.warn('AuthService: Impossible de construire l\'URL de logout Keycloak, fallback sur méthode librairie.');
            this.oauthService.logOut();
        }
    }

    private buildLogoutUrl(idToken: string | null): string | null {
        const issuer = (environment as any).orionUrl;
        if (!issuer) return null;

        // Construire l'URL de fin de session Keycloak
        // Note: '/protocol/openid-connect/logout' est standard pour Keycloak
        const endSessionEndpoint = `${issuer}/protocol/openid-connect/logout`;

        // Utiliser location.origin + /login comme post_logout_redirect_uri par défaut
        // ou utiliser deconnexionUri si défini
        const postLogoutRedirectUri = (environment as any).deconnexionUri || (window.location.origin + '/login');

        let url = `${endSessionEndpoint}?post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}&client_id=${encodeURIComponent((environment as any).clientId || '')}`;

        if (idToken) {
            url += `&id_token_hint=${encodeURIComponent(idToken)}`;
        }

        return url;
    }

    // Getter pour obtenir le jeton d'accès
    get token(): string | null {
        return this.oauthService.getAccessToken();
    }

    // Vérifier si l'utilisateur est authentifié
    isAuthenticated(): boolean {
        return this.oauthService.hasValidAccessToken();
    }

    // Méthode pour initier le flux de connexion manuellement
    login(returnUrl?: string): void {
        console.log('AuthService: Initiation du flux de connexion. Return URL:', returnUrl);
        this.initialLoginComplete = false; // Réinitialiser pour cette nouvelle tentative de connexion

        if (returnUrl) {
            sessionStorage.setItem(REDIRECT_STORAGE_KEY, returnUrl);
        }

        // Ne plus encoder manuellement car la librairie s'en charge déjà,
        // ce qui provoquait un double encodage (%252F).
        const state = returnUrl || undefined;
        this.oauthService.initLoginFlow(state);
    }

    // Méthode pour traiter le callback de redirection OAuth
    tryLogin(): Promise<boolean> {
        console.log('AuthService: Tentative de login.');
        return this.ensureDiscoveryDocumentAndLogin().then((isLoggedIn) => {
            console.log('AuthService: Tentative de login terminée.', isLoggedIn);
            return isLoggedIn && this.oauthService.hasValidAccessToken();
        });
    }

    // Getter pour accéder aux événements OAuth
    get events() {
        return this.oauthService.events;
    }

    finalizeLoginRedirect(): void {
        if (this.oauthService.hasValidAccessToken() && !this.initialLoginComplete) {
            this.completeInitialLogin();
        }
    }

    private completeInitialLogin(): void {
        this.initialLoginComplete = true;
        const targetUrl = this.consumeRedirectUrl();
        console.log('AuthService: Redirection vers', targetUrl);
        this.router.navigateByUrl(targetUrl, { replaceUrl: true });
    }

    private consumeRedirectUrl(): string {
        const rawState = (this.oauthService.state && this.oauthService.state !== 'undefined') ? this.oauthService.state : null;
        const stateUrl = rawState ? safeDecode(rawState) : null;
        const storedUrl = sessionStorage.getItem(REDIRECT_STORAGE_KEY) || localStorage.getItem(REDIRECT_STORAGE_KEY);

        console.log('AuthService: consumeRedirectUrl diagnostics:', {
            rawState,
            stateUrl,
            storedUrl,
            currentPath: window.location.pathname
        });

        if (storedUrl) {
            sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
            localStorage.removeItem(REDIRECT_STORAGE_KEY);
        }

        // Ne pas utiliser l'URL courante si c'est une URL de callback OAuth ou de login
        const currentPath = window.location.pathname;
        const isLoginPath = currentPath === '/login' || currentPath.startsWith('/login');
        const hasOAuthParams = window.location.search.includes('code=') ||
            window.location.search.includes('state=') ||
            window.location.search.includes('iss=');

        // Valider que stateUrl n'est pas une URL de login
        const isValidStateUrl = stateUrl && !stateUrl.startsWith('/login');
        // Valider que storedUrl n'est pas une URL de login
        const isValidStoredUrl = storedUrl && !storedUrl.startsWith('/login');

        console.log('AuthService: Redirection validation:', {
            isValidStateUrl,
            isValidStoredUrl,
            isLoginPath,
            hasOAuthParams
        });

        if (isValidStateUrl) return stateUrl!;
        if (isValidStoredUrl) return storedUrl!;
        if (!isLoginPath && !hasOAuthParams) return currentPath;

        const fallback = ((environment as any).redirectPage as string) || ((environment as any).homePath as string) || '/';
        console.log('AuthService: Redirection fallback vers:', fallback);
        return fallback;
    }

    private ensureDiscoveryDocumentAndLogin(): Promise<boolean> {
        if (!this.discoveryLoginPromise) {
            this.discoveryLoginPromise = this.oauthService
                .loadDiscoveryDocumentAndTryLogin()
                .finally(() => {
                    this.discoveryLoginPromise = null;
                });
        }
        return this.discoveryLoginPromise;
    }
}

const safeDecode = (value: string): string => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};
